import {useState, useRef, useEffect} from 'react';
import {Obsidian, MetaData} from '../controllers/Obsidian';
import {
  CurrentSongContextProviderValue,
  useCurrentSongContext,
} from '../context/CurrentSongProvider';
import {ObsidianContextProviderValue} from '../context/ObsidianProvider';

import {Platform} from '../types';
import {useMusicKitContext} from '../context/MusicKitProvider';
import {useQueueContext} from '../context/QueueProvider';
import {mockCatalogSongs} from '../components/Pages/Library/mockData';

// This function acts as a functional wrapper for the Obsidian class instance.
// To notify a component of a obsidian event, modify the obsidianActionHooks below, or add a new hook.
// If you add a new hook, make sure to trigger it in the controllers of all platforms.
function useObsidian(
  currentSong: CurrentSongContextProviderValue,
): ObsidianContextProviderValue {
  const [spotifySDKReady, setSpotifySDKReady] = useState(false);
  const [connected, setConnected] = useState(false);
  const musicKit = useMusicKitContext();

  const [error, setError] = useState<string | null | undefined>(null);
  const {timer} = useCurrentSongContext();
  const {queue, upNextQueue, refresh} = useQueueContext();

  // todo: Replace with a new fn currentSong.setEmpty();
  function setPlayerToEmptyState() {
    timer.reset();
    currentSong.setCurrentSong(null);
    currentSong.setSongMetaData(undefined);
    currentSong.setBuffer(0);
  }

  // Obsidian Action hooks (For class instance)

  function handleNewSong() {
    // Why this is here: obsidian.play does not automatically updated the `currentSong`
    // field in the currentSong context. Only the footer handleNext does that, so
    // we need to set currentSong to something if a song is played elsewhere like library page
    // This will be fixed once Song type refactor is merged.
    currentSong.setCurrentSong(mockCatalogSongs[0]);
    currentSong.setMusicLoading(true);
    currentSong.setBuffer(0);
    timer.reset();
  }

  function handlePlay() {
    currentSong.setPlaying(true);
    timer.start();
  }

  function handlePause() {
    currentSong.setPlaying(false);
    timer.stop();
  }

  function handleMetaDataChange(metaData: MetaData) {
    // Metadata changing is a better indicator for when to stop showing
    // skeleton components (controlled by `musicLoading` ctx) than handlePlay because
    // metaData often comes in from musicKit slightly after the song begins playing
    currentSong.setMusicLoading(false);
    currentSong.setSongMetaData(metaData);
    if (metaData.position) {
      timer.set(metaData.position);
    }
  }

  function handleBadPlayerState(error: string) {
    setError(error);
  }

  // TODO: Add `connected` state for each platform.
  function handleConnected(platform: Platform) {
    setConnected(true);
  }

  function handleSongFinished(emptyQueue?: boolean) {
    refresh();
    if (emptyQueue) {
      setPlayerToEmptyState();
    }
  }

  function handleBufferChange(percent: number) {
    currentSong.setBuffer(percent);
  }

  const obsidianActionHooks = {
    onNewSong: handleNewSong,
    onPlay: handlePlay,
    onPause: handlePause,
    onMetaDataChange: handleMetaDataChange,
    onBadPlayerState: handleBadPlayerState,
    onConnected: handleConnected,
    onSetVolume: handleSetVolume,
    onSeekTo: handleSeekTo,
    onStopTimer: () => timer.stop(),
    onSongFinished: (emptyQueue?: boolean) => handleSongFinished(emptyQueue),
    onBufferChange: handleBufferChange,
  };

  const {current: obsidian} = useRef(
    new Obsidian(obsidianActionHooks, queue, upNextQueue),
  );

  // Context setters

  function handleFooterPlay() {
    if (currentSong.song != null) {
      obsidian.togglePlay();
    } else {
      const nextSong = currentSong.next();
      if (nextSong) {
        obsidian.play(nextSong);
      } else {
      }
    }
  }

  function handleFooterPause() {
    obsidian.togglePlay();
  }

  function handleFooterForward() {
    const nextSong = currentSong.next();
    if (nextSong) {
      obsidian.play(nextSong);
    } else {
      obsidian.pause();
      handleSeekTo(0);
      setPlayerToEmptyState();
    }
  }

  function handleFooterBackward() {
    timer.stop();
    if (currentSong.song != null) {
      obsidian.seek(0);
    }
  }

  // todo: may be needed if changing volume outside footer slider
  function handleSetVolume(volume: number) {}

  function handleSeekTo(position: number) {
    timer.set(position);
    timer.start();
  }

  // Initialize Platforms that are linked but not connected.
  // TODO: Handle Timeouts + make sure these hooks re-run when user links
  // new platform. Idea for this: Create a UserContext which has linked platforms list in state.
  // And pass the context's state here as dependencies for these effects.
  useEffect(() => {
    const {
      linked: spotifyLinked,
      connected: spotifyConnected,
    } = obsidian.getPlatformState(Platform.Soundcloud);
    if (spotifyLinked && !spotifyConnected) {
      obsidian.initializeSoundcloud('XRQ3Ms16OoOSxwm77RIyz5KMydn1XfJO'); // TODO: Replace SoundCloud dev client_id, use env secret
    }
    const {
      linked: amLinked,
      connected: amConnected,
    } = obsidian.getPlatformState(Platform.AppleMusic);
    if (amLinked && !amConnected && musicKit) {
      obsidian.initializeAppleMusic(); // TODO: Replace SoundCloud dev client_id, use env secret
    }
  }, [obsidian, musicKit]);

  useEffect(() => {
    const {linked, connected} = obsidian.getPlatformState(Platform.Spotify);
    if (spotifySDKReady && linked && !connected) {
      // onRefreshSpotifyToken();
    }
  }, [obsidian, spotifySDKReady]);

  // Note: For Spotify / deprecated
  // function handleSpotifyAccessTokenCallback({refresh: accessToken}: any) {
  //   if (!accessToken) {
  //     // setError('useObsidian error: No spotify refresh token received');
  //     return;
  //   }
  //   obsidian.initializeSpotify(accessToken);
  // }

  if (!window.onSpotifyWebPlaybackSDKReady) {
    window.onSpotifyWebPlaybackSDKReady = () => {
      setSpotifySDKReady(true);
    };
  }

  return {
    obsidian,
    connected,
    error,
    handlePlay: handleFooterPlay,
    handlePause: handleFooterPause,
    handleForward: handleFooterForward,
    handleBackward: handleFooterBackward,
  };
}

export default useObsidian;
